import { pgTable, text, serial, integer, boolean, timestamp, json } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { relations } from "drizzle-orm";

// Enums
export enum ExerciseType {
  REPS = "reps",
  DURATION = "duration"
}

// Base tables for database
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  name: text("name").notNull(),
  maxJokerDays: integer("max_joker_days").notNull().default(2),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const usersRelations = relations(users, ({ many }) => ({
  workouts: many(workouts),
}));

export const exercises = pgTable("exercises", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  exerciseType: text("exercise_type").notNull(),
  initialSets: integer("initial_sets"),
  initialReps: integer("initial_reps"),
  initialDuration: integer("initial_duration"),
  frequency: text("frequency"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const exercisesRelations = relations(exercises, ({ many }) => ({
  workoutExercises: many(workoutExercises),
}));

export const workouts = pgTable("workouts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  date: timestamp("date").notNull(),
  isJokerDay: boolean("is_joker_day").notNull().default(false),
  completedExercises: integer("completed_exercises").notNull().default(0),
  totalExercises: integer("total_exercises").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const workoutsRelations = relations(workouts, ({ one, many }) => ({
  user: one(users, {
    fields: [workouts.userId],
    references: [users.id],
  }),
  workoutExercises: many(workoutExercises),
}));

export const workoutExercises = pgTable("workout_exercises", {
  id: serial("id").primaryKey(),
  workoutId: integer("workout_id").notNull(),
  exerciseId: integer("exercise_id").notNull(),
  sets: integer("sets"),
  reps: integer("reps"),
  duration: integer("duration"),
  completed: boolean("completed").notNull().default(false),
  actualSets: integer("actual_sets"),
  actualReps: integer("actual_reps"),
  actualDuration: integer("actual_duration"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const workoutExercisesRelations = relations(workoutExercises, ({ one }) => ({
  workout: one(workouts, {
    fields: [workoutExercises.workoutId],
    references: [workouts.id],
  }),
  exercise: one(exercises, {
    fields: [workoutExercises.exerciseId],
    references: [exercises.id],
  }),
}));

// Schemas for insertion and validation
export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  name: true,
  maxJokerDays: true,
});

export const insertExerciseSchema = createInsertSchema(exercises).pick({
  name: true,
  description: true,
  exerciseType: true,
  initialSets: true,
  initialReps: true,
  initialDuration: true,
  frequency: true,
});

export const insertWorkoutSchema = createInsertSchema(workouts).pick({
  userId: true,
  date: true,
  isJokerDay: true,
  completedExercises: true,
  totalExercises: true,
});

export const insertWorkoutExerciseSchema = createInsertSchema(workoutExercises).pick({
  workoutId: true,
  exerciseId: true,
  sets: true,
  reps: true,
  duration: true,
  completed: true,
  actualSets: true,
  actualReps: true,
  actualDuration: true,
});

// Types derived from schemas
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type Exercise = typeof exercises.$inferSelect;
export type InsertExercise = z.infer<typeof insertExerciseSchema>;

export type Workout = typeof workouts.$inferSelect;
export type InsertWorkout = z.infer<typeof insertWorkoutSchema>;

export type WorkoutExercise = typeof workoutExercises.$inferSelect;
export type InsertWorkoutExercise = z.infer<typeof insertWorkoutExerciseSchema>;

// Extended types for API
export interface CompleteWorkout extends Workout {
  exercises: (WorkoutExercise & { exercise: Exercise })[];
  requestedDate?: string;
  requestedDateString?: string;
  requestedDateComponents?: {
    year: number;
    month: number;
    day: number;
    dayOfWeek: number;
  };
}

export interface UserWeekSummary {
  weekNumber: number;
  year: number;
  jokerDaysTaken: number;
  maxJokerDays: number;
}

// Custom interface for exercise sets (used in frontend)
export interface ExerciseSet {
  setNumber: number;
  targetReps?: number;
  targetDuration?: number;
  actualReps?: number;
  actualDuration?: number;
  completed: boolean;
}

// Workout summary for progress display
export interface WorkoutSummary {
  id: number;
  date: Date;
  title: string;
  completedExercises: number;
  totalExercises: number;
  isJokerDay: boolean;
}

// Weekly activity for progress charts
export interface WeeklyActivity {
  day: string;
  percentage: number;
  isJokerDay: boolean;
}
